import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { scaleLinear } from "d3";
import Region from "../../../../models/Region";
import getBaseLen from "../../helpers/getBaseLen";

function calcOffset(middle, txtWidth, maxWidth) {
  const halfWidth = txtWidth / 2;
  const leftOffset = middle - halfWidth;
  const rightOffset = maxWidth - (middle + halfWidth);

  if (leftOffset < 0) {
    return -leftOffset;
  }
  if (rightOffset < 0) {
    return rightOffset;
  }

  return 0;
}

function QueryLocationGraph({ start, end, region, fillColor = "#9BD8F1" }) {
  const [txtOffset, setTxtOffset] = useState(0);
  const txtRef = React.useRef(null);
  const width = 424;
  const height = 60;
  const g0 = Math.min(start, region.g0);
  const g1 = Math.max(end, region.g1);
  const paddingX = 60;
  const paddingY = 10;
  const x0 = 0 + paddingX;
  const x1 = width - paddingX;
  const y0 = 0 + paddingY;
  const y1 = height - paddingY;

  const xScale = scaleLinear().domain([g0, g1]).range([x0, x1]);
  const yScale = scaleLinear().domain([0, 120]).range([y0, y1]);

  // selected area
  const x0Sel = xScale(start);
  const x1Sel = xScale(end);
  const widthSel = x1Sel - x0Sel;
  const lengthSel = Math.abs(end - start);
  const middleSel = xScale(start + lengthSel / 2);
  const selText = getBaseLen(lengthSel);

  // current region
  const x0Reg = xScale(region.g0);
  const x1Reg = xScale(region.g1);
  const widthReg = x1Reg - x0Reg;
  const lengthReg = Math.abs(region.g1 - region.g0);
  const middleReg = xScale(region.g0 + lengthReg / 2);
  const regText = getBaseLen(lengthReg);

  function textAlign(posX) {
    if (posX < width / 2) {
      return "end";
    }
    return "start";
  }

  const overlap = region.calcOverlap(start, end);
  const overlapNorm = [...overlap].sort();
  const overlapLength = overlap[1] - overlap[0];
  const overlapMiddle = xScale(overlap[0] + overlapLength / 2);
  const overlapText = getBaseLen(Math.abs(overlapLength));
  const percent = overlapLength / lengthReg;
  const percentStyle = { style: "percent" };
  const overlapPercent = percent.toLocaleString("en", percentStyle);
  const overlapTxt = percent < 0.01 ? "" : `(${overlapPercent} of variant)`;
  const overlapLeftTxt = overlapNorm[0] - g0;
  const overlapRightTxt = g1 - overlapNorm[1];

  useEffect(() => {
    const el = txtRef.current;
    const elWidth = el ? el.getBoundingClientRect()?.width : 0;
    const offset = calcOffset(overlapMiddle, elWidth, width);
    setTxtOffset(offset);
  }, [overlapTxt, width, overlapMiddle]);

  return (
    <div className="tw-pt-2 tw-mt-2 tw-border-t border-normal tw-flex">
      <div className="tw-flex-grow" />
      <div>
        <div className="tw-font-semibold tw-text-center ">
          Relative location of your query
          <i className="fa fa-circle color-3 tw-text-3xs tw-mx-0.5" />
          <span className="tw-px-0.5" />
          and this variant
          <i
            style={{ color: fillColor }}
            className="fa fa-circle color-3 tw-text-3xs tw-mx-0.5"
          />
        </div>

        <div
          className="tw-mx-auto tw-text-2xs tw-lowercase tw-overflow-hidden"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <svg width={width} height={height}>
            <pattern
              id="diagonalHatchRed"
              width="4"
              height="4"
              patternTransform="rotate(45 0 0)"
              patternUnits="userSpaceOnUse"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="10"
                className="tw-text-blueGray-400 tw-stroke tw-stroke-current"
              />
            </pattern>
            <pattern
              id="diagonalHatchGray"
              width="4"
              height="4"
              patternTransform="rotate(45 0 0)"
              patternUnits="userSpaceOnUse"
              className="color-3"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="10"
                className="tw-stroke tw-stroke-current tw-opacity-40"
              />
            </pattern>

            <rect
              x={xScale(g0)}
              y={yScale(0)}
              width={xScale(g1) - xScale(g0)}
              height={yScale(90) - yScale(0)}
              className="tw-fill-current tw-text-blueGray-400 tw-stroke-current tw-opacity-20"
            />

            {/* overlap rect */}
            {overlapLength > 0 ? (
              <>
                <rect
                  x={xScale(overlap[0]) - 0.5}
                  y={yScale(0)}
                  width={xScale(overlap[1]) - xScale(overlap[0]) + 1}
                  height={yScale(90) - yScale(0)}
                  style={{ fill: "url(#diagonalHatchRed)" }}
                  data-tooltip="overlap of your query with CNV"
                  data-tooltip-class="genome-browser-small-tooltip"
                />
              </>
            ) : null}

            <rect
              x={x0Sel}
              y={yScale(0)}
              width={widthSel}
              height={yScale(20) - yScale(0)}
              className="tw-fill-current color-3 tw-stroke-current"
              data-tooltip="your query"
              data-tooltip-class="genome-browser-small-tooltip"
            />
            <text
              x={middleSel >= width / 2 ? x1Sel + 3 : x0Sel - 3}
              textAnchor={textAlign(middleSel)}
              y={yScale(10)}
              dy="0.33em"
              className="color-1 tw-fill-current"
              data-tooltip="your query"
              data-tooltip-class="genome-browser-small-tooltip"
            >
              {selText}
            </text>

            {/* cnv */}
            <rect
              x={x0Reg}
              width={widthReg}
              y={yScale(40)}
              height={yScale(56) - yScale(40)}
              style={{ fill: fillColor, stroke: fillColor }}
              className="tw-stroke-2"
              data-tooltip="CNV"
              data-tooltip-class="genome-browser-small-tooltip"
            />
            <text
              x={middleReg >= width / 2 ? x1Reg + 3 : x0Reg - 3}
              textAnchor={textAlign(middleReg)}
              y={yScale(48)}
              dy="0.33em"
              data-tooltip="CNV"
              data-tooltip-class="genome-browser-small-tooltip"
              className="color-1 tw-fill-current tw-opacity-80"
            >
              {regText}
            </text>

            <line
              x1={xScale(overlap[0])}
              x2={xScale(overlap[0])}
              y1={yScale(70)}
              y2={yScale(90)}
              className="tw-stroke tw-stroke-current color-1 tw-opacity-70"
            />
            {/* overlap vertical 2 */}
            <line
              x1={xScale(overlap[1])}
              x2={xScale(overlap[1])}
              y1={yScale(70)}
              y2={yScale(90)}
              className="tw-stroke tw-stroke-current color-1 tw-opacity-70"
            />

            {/* overlap text */}
            <text
              ref={txtRef}
              x={overlapMiddle}
              y={yScale(100)}
              textAnchor="middle"
              dy="1rem"
              transform={`translate(${txtOffset}, 0)`}
              className="color-1 tw-fill-current tw-font-bold"
            >
              {overlapLength > 0 ? "Overlap" : "distance"}: {overlapText}{" "}
              {overlapLength > 1 ? overlapTxt : ""}
            </text>

            {overlapLength > 0 ? (
              <>
                <line
                  x1={xScale(g0)}
                  x2={xScale(overlapNorm[0])}
                  y1={yScale(80)}
                  y2={yScale(80)}
                  className="tw-stroke tw-stroke-current color-3 tw-opacity-70"
                />
                <line
                  x1={xScale(overlapNorm[1])}
                  x2={xScale(g1)}
                  y1={yScale(80)}
                  y2={yScale(80)}
                  className="tw-stroke tw-stroke-current color-3 tw-opacity-70"
                />
                {overlapLeftTxt ? (
                  <text
                    x={xScale(g0) - 2}
                    y={yScale(80)}
                    textAnchor="end"
                    dy=".35em"
                    className="color-3 tw-fill-current tw-opacity-80"
                  >
                    {getBaseLen(overlapLeftTxt)}
                  </text>
                ) : null}
                {overlapRightTxt ? (
                  <text
                    x={xScale(g1) + 3}
                    y={yScale(80)}
                    textAnchor="start"
                    dy=".35em"
                    className="color-3 tw-fill-current tw-opacity-80"
                  >
                    {getBaseLen(overlapRightTxt)}
                  </text>
                ) : null}
              </>
            ) : (
              <line
                x1={xScale(overlapNorm[0])}
                x2={xScale(overlapNorm[1])}
                y1={yScale(80)}
                y2={yScale(80)}
                className="tw-stroke tw-stroke-current color-1 tw-opacity-70"
              />
            )}
          </svg>
        </div>
      </div>
      <div className="tw-flex-grow" />
    </div>
  );
}

QueryLocationGraph.propTypes = {
  start: PropTypes.number.isRequired,
  end: PropTypes.number.isRequired,
  region: PropTypes.instanceOf(Region).isRequired,
  fillColor: PropTypes.string.isRequired,
};

export default QueryLocationGraph;
